import styled, { css } from 'styled-components';

const AVATAR_OVERLAP_REM = '5rem';
const AVATAR_SIZE_REM = '8.4rem';

export const Banner = styled.div<{ $url?: string }>`
  display: ${({ $url }) => ($url ? 'block' : 'none')};
  width: 100%;
  height: 16.5rem;
  border-radius: 1.6rem;
  overflow: hidden;
  ${({ $url }) =>
    $url &&
    css`
      background: url(${$url}) center/cover no-repeat;
    `}
`;

export const Identity = styled.div<{ $hasBanner?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  ${({ $hasBanner }) =>
    $hasBanner &&
    css`
      margin-top: -${AVATAR_OVERLAP_REM};
    `}
`;

export const Avatar = styled.div<{
  $accent: string;
  $rounded: boolean;
  $borderColor: string;
  $borderWidth: number;
}>`
  width: ${AVATAR_SIZE_REM};
  height: ${AVATAR_SIZE_REM};
  border-radius: ${({ $rounded }) => ($rounded ? '50%' : '1.6rem')};
  border: ${({ $borderWidth, $borderColor }) =>
    `${$borderWidth / 10}rem solid ${$borderColor}`};
  background-color: ${({ $accent }) => $accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

export const AvatarImage = styled.img<{ $rounded: boolean; $logo: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $logo }) => ($logo ? 'contain' : 'cover')};
  border-radius: ${({ $rounded }) => ($rounded ? '50%' : '1.3rem')};
  background-color: ${({ $logo, theme }) =>
    $logo ? theme.colors.white : 'transparent'};
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  justify-content: center;
`;

export const Name = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: 0.4rem;
  text-align: center;
`;

export const SecondaryLogo = styled.img`
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.6rem;
  object-fit: cover;
`;

export const Title = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.slate};
  text-transform: capitalize;
  text-align: center;
  margin-top: 0.4rem;
`;

export const Tagline = styled.div`
  margin-top: 0.6rem;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slateLight};
  font-style: italic;
  text-align: center;
  max-width: 52rem;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

export const JoinedText = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-align: center;
`;

export const MetaSeparator = styled.span`
  color: ${({ theme }) => theme.colors.slateMuted};
  font-size: 1.2rem;
`;

export const CollabBadge = styled.span<{ $accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 1rem;
  border-radius: 999rem;
  background: ${({ $accent }) => `${$accent}1F`};
  color: ${({ $accent }) => $accent};
  border: 0.1rem solid ${({ $accent }) => `${$accent}52`};
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.01rem;
`;

export const SocialIconsRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

export const SocialTile = styled.a<{ $background: string }>`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: ${({ $background }) => $background};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

export const ActionRowWrap = styled.div`
  margin-top: 1.6rem;
`;

export const ActionRowButtons = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

export const PrimaryAction = styled.button<{ $accent: string }>`
  flex: 1;
  min-width: 12rem;
  padding: 1rem 1.4rem;
  border-radius: 1rem;
  border: none;
  background: ${({ $accent }) => $accent};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.4rem;
  font-weight: 700;
  cursor: pointer;
`;

export const SecondaryAction = styled.button<{ $accent: string }>`
  flex: 1;
  min-width: 12rem;
  padding: 1rem 1.4rem;
  border-radius: 1rem;
  border: 0.1rem solid ${({ $accent }) => `${$accent}52`};
  background: ${({ $accent }) => `${$accent}1A`};
  color: ${({ $accent }) => $accent};
  font-size: 1.4rem;
  font-weight: 700;
  cursor: pointer;
`;

export const SecondaryLinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1rem;
  font-size: 1.3rem;
`;

export const SecondaryLinksLabel = styled.span`
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const SecondaryLinksSep = styled.span`
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const SecondaryLink = styled.button<{ $accent: string }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${({ $accent }) => $accent};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const ResponseTimeText = styled.div`
  text-align: center;
  margin-top: 0.8rem;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const ScoreCard = styled.div<{ $accent: string }>`
  border-radius: 1.6rem;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
  background: ${({ $accent }) => `${$accent}1A`};
  border: 0.1rem solid ${({ $accent }) => `${$accent}33`};
`;

export const JobsCompleted = styled.div<{ $accent: string }>`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 0.1rem solid ${({ $accent }) => `${$accent}33`};
`;

export const JobsCompletedValue = styled.span<{ $accent: string }>`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${({ $accent }) => $accent};
  line-height: 1;
`;

export const JobsCompletedLabel = styled.span<{ $accent: string }>`
  font-size: 1.15rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  color: ${({ $accent }) => `${$accent}BF`};
`;

export const ScoreLabel = styled.div<{ $accent: string }>`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.15rem;
  text-transform: uppercase;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ $accent }) => $accent};
`;

export const ScoreValue = styled.div<{ $accent: string }>`
  font-size: 3.6rem;
  font-weight: 800;
  line-height: 4rem;
  color: ${({ $accent }) => $accent};
`;

export const ScoreStars = styled.div`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.amber};
  margin-top: 0.4rem;
  letter-spacing: 0.2rem;
`;

export const ScoreBased = styled.div<{ $accent: string }>`
  font-size: 1.3rem;
  margin-top: ${({ theme }) => theme.spacing.xs};
  color: ${({ $accent }) => `${$accent}BF`};
`;

export const PromotesCard = styled.div`
  margin-top: 1.2rem;
  padding: 1rem 1.2rem;
  border-radius: 1.2rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
`;

export const PromotesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.8rem;
`;

export const PromotesLabel = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
`;

export const PromotesPreview = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  font-weight: 600;
  flex: 1;
`;

export const PromotesNiche = styled.span<{ $accent: string }>`
  padding: 0.2rem 0.8rem;
  border-radius: 999rem;
  background: ${({ $accent }) => $accent};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.1rem;
  font-weight: 700;
`;

export const PromotesCaret = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-left: auto;
`;

export const PromotesChips = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;
`;

export const PromotesChip = styled.span`
  padding: 0.3rem 1rem;
  border-radius: 999rem;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  font-weight: 600;
`;

export const FeaturedTeamWrap = styled.div`
  margin-top: 1.2rem;
`;

export const FeaturedTeamLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  margin-bottom: 0.8rem;
`;

export const FeaturedTeamRow = styled.div`
  display: flex;
  gap: 1.2rem;
  overflow-x: auto;
`;

export const FeaturedTeamMember = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 6.8rem;
  gap: 0.4rem;
`;

export const FeaturedAvatar = styled.div<{ $accent: string; $url?: string }>`
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 50%;
  background: ${({ $url, $accent }) =>
    $url ? `url(${$url}) center/cover no-repeat` : $accent};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
`;

export const FeaturedName = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  font-weight: 700;
  text-align: center;
  max-width: 8rem;
`;

export const FeaturedRole = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-align: center;
`;

export const TiktokWrap = styled.span`
  position: relative;
  width: 1.6rem;
  height: 1.6rem;
  display: inline-block;
`;

export const TiktokLayer = styled.span<{ $offset: number }>`
  position: absolute;
  left: ${({ $offset }) => `${$offset / 10}rem`};
  top: 0;
`;
